import { useRef } from "react";
import { api, resolveFileUrl } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Trash2, ImagePlus, X } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);

export const QUESTION_TYPES = [
  { type: "mcq",         label: "اختيار واحد (راديو)" },
  { type: "checkbox",    label: "عدة إجابات (تشيك بوكس)" },
  { type: "dropdown",    label: "قائمة منسدلة" },
  { type: "tf",          label: "صح / خطأ" },
  { type: "matching",    label: "توصيل (سحب وإفلات)" },
  { type: "image_label", label: "صورة مرقّمة" },
  { type: "short",       label: "إجابة قصيرة" },
];

export const TYPE_LABELS = Object.fromEntries(QUESTION_TYPES.map(t => [t.type, t.label]));

export function newQuestion(type) {
  const q = { id: `q_${uid()}`, type, text: "", points: 1 };
  if (type === "mcq" || type === "dropdown") { q.options = [{ id: uid(), text: "" }, { id: uid(), text: "" }]; q.correct = ""; }
  if (type === "checkbox") { q.options = [{ id: uid(), text: "" }, { id: uid(), text: "" }]; q.correct = []; }
  if (type === "tf") q.correct = true;
  if (type === "short") q.correct = "";
  if (type === "matching") q.pairs = [{ id: uid(), left: "", right: "" }, { id: uid(), left: "", right: "" }];
  if (type === "image_label") { q.labels = [{ num: "1", correct: "" }]; q.image_url = null; }
  return q;
}

export function QuestionEditor({ q, idx, onChange, onRemove }) {
  const fileRef = useRef(null);

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/quizzes/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ image_url: data.url });
      toast.success("تم رفع الصورة");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "فشل رفع الصورة (الحد 500 ك.ب)");
    }
  };

  const setOpt = (oi, text) => {
    const options = [...(q.options || [])]; options[oi] = { ...options[oi], text };
    onChange({ options });
  };
  const addOpt = () => onChange({ options: [...(q.options || []), { id: uid(), text: "" }] });
  const rmOpt = (oi) => {
    const removed = q.options[oi];
    const options = q.options.filter((_, i) => i !== oi);
    const patch = { options };
    if (q.type === "checkbox") patch.correct = (q.correct || []).filter(id => id !== removed.id);
    else if (q.correct === removed.id) patch.correct = "";
    onChange(patch);
  };
  const toggleCheckCorrect = (oid) => {
    const cur = Array.isArray(q.correct) ? q.correct : [];
    onChange({ correct: cur.includes(oid) ? cur.filter(x => x !== oid) : [...cur, oid] });
  };
  const setPair = (pi, patch) => {
    const pairs = [...(q.pairs || [])]; pairs[pi] = { ...pairs[pi], ...patch };
    onChange({ pairs });
  };
  const setLabel = (li, patch) => {
    const labels = [...(q.labels || [])]; labels[li] = { ...labels[li], ...patch };
    onChange({ labels });
  };

  return (
    <Card className="p-3 border" data-testid={`quiz-q-${idx}`}>
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-muted">{TYPE_LABELS[q.type] || q.type}</span>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" title="إرفاق صورة للسؤال" onClick={() => fileRef.current?.click()} data-testid={`q-img-btn-${idx}`}>
            <ImagePlus className="w-4 h-4 text-emerald-700" />
          </Button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => uploadImage(e.target.files?.[0])} />
          <Button variant="ghost" size="icon" onClick={onRemove}><Trash2 className="w-4 h-4 text-destructive" /></Button>
        </div>
      </div>
      <Input placeholder="نص السؤال" value={q.text} onChange={e => onChange({ text: e.target.value })} className="mb-2" data-testid={`q-text-${idx}`} />
      {q.image_url && (
        <div className="relative inline-block mb-2">
          <img src={resolveFileUrl(q.image_url)} alt="" className="max-h-44 rounded-lg border" />
          <button className="absolute top-1 end-1 bg-white/90 rounded-full p-0.5 border" onClick={() => onChange({ image_url: null })}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      <div className="flex gap-2 items-center mb-2">
        <Label className="text-xs">درجات:</Label>
        <Input type="number" value={q.points} onChange={e => onChange({ points: Number(e.target.value) || 1 })} className="w-20" />
      </div>

      {(q.type === "mcq" || q.type === "dropdown") && (
        <div className="space-y-2">
          {(q.options || []).map((o, oi) => (
            <div key={o.id} className="flex gap-2 items-center">
              <input type="radio" name={`correct-${q.id}`} checked={q.correct === o.id} onChange={() => onChange({ correct: o.id })} />
              <Input value={o.text} onChange={e => setOpt(oi, e.target.value)} placeholder={`الخيار ${oi + 1}`} />
              {(q.options.length > 2) && <Button variant="ghost" size="icon" onClick={() => rmOpt(oi)}><X className="w-4 h-4" /></Button>}
            </div>
          ))}
          <Button size="sm" variant="ghost" onClick={addOpt}>+ خيار</Button>
          <p className="text-[11px] text-muted-foreground">اختر الدائرة بجانب الإجابة الصحيحة{q.type === "dropdown" ? " — ستظهر للطالب كقائمة منسدلة." : "."}</p>
        </div>
      )}

      {q.type === "checkbox" && (
        <div className="space-y-2">
          {(q.options || []).map((o, oi) => (
            <div key={o.id} className="flex gap-2 items-center">
              <input type="checkbox" checked={(q.correct || []).includes(o.id)} onChange={() => toggleCheckCorrect(o.id)} />
              <Input value={o.text} onChange={e => setOpt(oi, e.target.value)} placeholder={`الخيار ${oi + 1}`} />
              {(q.options.length > 2) && <Button variant="ghost" size="icon" onClick={() => rmOpt(oi)}><X className="w-4 h-4" /></Button>}
            </div>
          ))}
          <Button size="sm" variant="ghost" onClick={addOpt}>+ خيار</Button>
          <p className="text-[11px] text-muted-foreground">علّم كل الإجابات الصحيحة (يمكن أكثر من واحدة). التصحيح جزئي مع خصم للاختيارات الخاطئة.</p>
        </div>
      )}

      {q.type === "tf" && (
        <div className="flex gap-3">
          <label className="flex items-center gap-2"><input type="radio" checked={q.correct === true} onChange={() => onChange({ correct: true })} />صح / نعم</label>
          <label className="flex items-center gap-2"><input type="radio" checked={q.correct === false} onChange={() => onChange({ correct: false })} />خطأ / لا</label>
        </div>
      )}

      {q.type === "short" && (
        <div>
          <Input value={q.correct || ""} onChange={e => onChange({ correct: e.target.value })} placeholder="الإجابة المتوقعة" />
          <label className="flex items-center gap-2 mt-2 text-xs">
            <input type="checkbox" checked={!!q.case_sensitive} onChange={e => onChange({ case_sensitive: e.target.checked })} />حساس لحالة الأحرف
          </label>
        </div>
      )}

      {q.type === "matching" && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 text-[11px] font-semibold text-muted-foreground"><span>العنصر (سؤال)</span><span>المطابق له (إجابة)</span></div>
          {(q.pairs || []).map((p, pi) => (
            <div key={p.id} className="flex gap-2 items-center">
              <Input value={p.left} onChange={e => setPair(pi, { left: e.target.value })} placeholder={`عنصر ${pi + 1}`} />
              <span className="text-muted-foreground">↔</span>
              <Input value={p.right} onChange={e => setPair(pi, { right: e.target.value })} placeholder="الإجابة المطابقة" />
              {(q.pairs.length > 2) && <Button variant="ghost" size="icon" onClick={() => onChange({ pairs: q.pairs.filter((_, i) => i !== pi) })}><X className="w-4 h-4" /></Button>}
            </div>
          ))}
          <Button size="sm" variant="ghost" onClick={() => onChange({ pairs: [...(q.pairs || []), { id: uid(), left: "", right: "" }] })}>+ زوج</Button>
          <p className="text-[11px] text-muted-foreground">ستُعرض الإجابات مخلوطة للطالب ليسحب كل إجابة إلى عنصرها الصحيح.</p>
        </div>
      )}

      {q.type === "image_label" && (
        <div className="space-y-2">
          {!q.image_url && (
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid={`q-imagelabel-upload-${idx}`}>
              <ImagePlus className="w-4 h-4 me-2" />ارفع صورة عليها أرقام
            </Button>
          )}
          <div className="grid grid-cols-2 gap-2 text-[11px] font-semibold text-muted-foreground"><span>الرقم على الصورة</span><span>الإجابة الصحيحة</span></div>
          {(q.labels || []).map((lb, li) => (
            <div key={li} className="flex gap-2 items-center">
              <Input value={lb.num} onChange={e => setLabel(li, { num: e.target.value })} className="w-20 text-center" />
              <Input value={lb.correct} onChange={e => setLabel(li, { correct: e.target.value })} placeholder="الإجابة لهذا الرقم" />
              {(q.labels.length > 1) && <Button variant="ghost" size="icon" onClick={() => onChange({ labels: q.labels.filter((_, i) => i !== li) })}><X className="w-4 h-4" /></Button>}
            </div>
          ))}
          <Button size="sm" variant="ghost" onClick={() => onChange({ labels: [...(q.labels || []), { num: String((q.labels?.length || 0) + 1), correct: "" }] })}>+ رقم</Button>
        </div>
      )}
    </Card>
  );
}
